import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  fetchLists,
  fetchPlayersForList,
  createList as apiCreateList,
  deleteList as apiDeleteList,
  addPlayerToList,
  updateListPlayerIds,
} from "../services/player-lists-service";

export function usePlayerLists() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [lists, setLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState(searchParams.get("listId") || "");
  const [players, setPlayers] = useState([]);
  const [filter, setFilter] = useState("");
  const [loadingLists, setLoadingLists] = useState(true);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedList = useMemo(
    () => lists.find((l) => l.id === selectedListId),
    [lists, selectedListId]
  );

  const loadLists = useCallback(async () => {
    setLoadingLists(true);
    try {
      const userLists = await fetchLists();
      setLists(userLists);
      if (!selectedListId && userLists.length > 0) setSelectedListId(userLists[0].id);
    } catch {
      toast.error("Failed to load lists");
    } finally {
      setLoadingLists(false);
    }
  }, [selectedListId]);

  const loadPlayersForList = useCallback(async (listId) => {
    if (!listId) return setPlayers([]);
    setLoadingPlayers(true);
    try {
      const result = await fetchPlayersForList(listId);
      setPlayers(result);
    } catch {
      toast.error("Failed to load players");
    } finally {
      setLoadingPlayers(false);
    }
  }, []);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  useEffect(() => {
    if (selectedListId) {
      setSearchParams({ listId: selectedListId });
      loadPlayersForList(selectedListId);
    } else {
      setSearchParams({});
      setPlayers([]);
    }
  }, [selectedListId, setSearchParams, loadPlayersForList]);

  const createList = useCallback(async (name) => {
    setSaving(true);
    try {
      const newList = await apiCreateList(name);
      setLists((prev) => [newList, ...prev]);
      setSelectedListId(newList.id);
      toast.success("List created");
    } catch {
      toast.error("Failed to create list");
    } finally {
      setSaving(false);
    }
  }, []);

  const deleteList = useCallback(
    async (list) => {
      setSaving(true);
      try {
        await apiDeleteList(list.id);
        const remaining = lists.filter((l) => l.id !== list.id);
        setLists(remaining);
        if (selectedListId === list.id) {
          setSelectedListId(remaining[0]?.id || "");
          setPlayers([]);
        }
        toast.success("List deleted");
      } catch {
        toast.error("Failed to delete list");
      } finally {
        setSaving(false);
      }
    },
    [lists, selectedListId]
  );

  const addPlayer = useCallback(
    async (player) => {
      if (!selectedListId) return;
      try {
        const playerIdStr = (player.player_id || "").toString();
        await addPlayerToList(selectedListId, playerIdStr);
        setLists((prev) =>
          prev.map((l) =>
            l.id === selectedListId
              ? { ...l, playerIds: Array.from(new Set([...(l.playerIds || []), playerIdStr])) }
              : l
          )
        );
        setPlayers((prev) => [...prev, player]);
        toast.success("Player added");
      } catch {
        toast.error("Failed to add player");
      }
    },
    [selectedListId]
  );

  const removePlayer = useCallback(
    async (playerId) => {
      if (!selectedListId) return;
      try {
        const current = lists.find((l) => l.id === selectedListId);
        const updatedIds = current.playerIds.filter((id) => id !== playerId.toString());
        await updateListPlayerIds(current, updatedIds);
        setLists((prev) =>
          prev.map((l) => (l.id === selectedListId ? { ...l, playerIds: updatedIds } : l))
        );
        setPlayers((prev) =>
          prev.filter((p) => (p.player_id || "").toString() !== playerId.toString())
        );
        toast.success("Player removed");
      } catch {
        toast.error("Failed to remove player");
      }
    },
    [selectedListId, lists]
  );

  const tableData = useMemo(() => {
    if (!filter) return players;
    const s = filter.toLowerCase();
    return players.filter(
      (p) =>
        p.player_name?.toLowerCase().includes(s) ||
        p.team_name?.toLowerCase().includes(s)
    );
  }, [players, filter]);

  return {
    lists,
    selectedListId,
    setSelectedListId,
    selectedList,
    players,
    filter,
    setFilter,
    tableData,
    loadingLists,
    loadingPlayers,
    saving,
    createList,
    deleteList,
    addPlayer,
    removePlayer,
  };
}
